import { create } from 'zustand';
import apiClient from '../service/apiClient';
import { Page, ResponseError } from '../type/type';
import { toast } from 'react-toastify';
import { NavigateFunction } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { formatDate } from '../util/util';

// 프롬프트 파라미터 타입
interface IPromptParam {
  code: string;
  desc: string;
  example: string;
}

// 프롬프트 타입
export interface IPrompt {
  id: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  title: string;
  content: string;
  promptCode: string;
}

// 프롬프트 응답 타입
interface PromptsResponse extends Page {
  content: IPrompt[];
}

// 프롬프트 등록 요청 타입
type PostPromptRequest = {
  title: string;
  content: string;
  promptCode: string;
};

// 프롬프트 업데이트 요청 타입
type UpdatePromptRequest = {
  title?: string;
  content?: string;
  promptCode?: string;
};

type PromptExcelUploadError = {
  rowNumber: number;
  promptCode: string;
  errorMsg: string;
};

type PromptExcelUploadResponse = {
  totalRows: number;
  successCount: number;
  failCount: number;
  errors: PromptExcelUploadError[];
};

// Zustand Store Data 타입
type PromptParamData = {
  // 프롬프트 목록
  prompts: IPrompt[];
  promptsPage: Page | null;

  // 프롬프트 상세
  promptDetail: IPrompt | null;

  // 프롬프트 파라미터
  promptParams: IPromptParam[];
};

// Zustand Store Action 타입
type PromptParamAction = {
  // 프롬프트 목록 가져오기
  getPrompts: (title?: string, page?: number) => Promise<void>;

  // 프롬프트 상세 그냥 setting 하기 (링크로 바로 들어간게 아니라 테이블 정보에서 가져가는 형식)
  setPromptDetail: (promptData: IPrompt) => void;

  // 프롬프트 상세 가져오기
  getPromptDetail: (id: string) => Promise<void>;

  // 프롬프트 파라미터 목록 가져오기
  getPrompmtParams: () => Promise<void>;

  // 프롬프트 등록
  postPrompt: (
    promptData: PostPromptRequest,
    navigate: NavigateFunction
  ) => Promise<void>;

  // 프롬프트 업데이트
  updatePrompt: (id: string, promptData: UpdatePromptRequest) => Promise<void>;

  downloadPromptExcelTemplate: () => Promise<void>;

  uploadPromptExcel: (file: File) => Promise<void>;
};

export const usePromptStore = create<PromptParamData & PromptParamAction>(
  (set, get) => ({
    // 프롬프트 목록
    prompts: [],
    promptsPage: null,

    // 프롬프트 상세
    promptDetail: null,

    // 프롬프트 파라미터
    promptParams: [],

    // 프롬프트 목록 가져오기
    getPrompts: async (title, page) => {
      set({ promptDetail: null }); // 프롬프트 상세 초기화

      try {
        const response = await apiClient.get<PromptsResponse>('/prompt', {
          params: {
            title,
            page,
          },
        });

        const { content, ...rest } = response.data;

        set({ prompts: content }); // 프롬프트 목록 세팅
        set({ promptsPage: rest }); // 프롬프트 페이지 세팅
      } catch (error) {
        console.error('API 요청 실패:', error);
        toast.error('프롬프트 목록을 가져오지 못했습니다.');
      }
    },

    // 프롬프트 상세 그냥 setting 하기
    setPromptDetail: promptData => {
      set({ promptDetail: promptData });
    },

    // 프롬프트 상세 가져오기
    getPromptDetail: async id => {
      try {
        const response = await apiClient.get<IPrompt>(`/prompt/${id}`);
        console.log(response.data);
        set({ promptDetail: response.data });
      } catch (error) {
        console.error('API 요청 실패:', error);
        toast.error('프롬프트 상세를 가져오지 못했습니다.');
      }
    },

    // 프롬프트 파라미터 목록 가져오기
    getPrompmtParams: async () => {
      try {
        const response = await apiClient.get<IPromptParam[]>('/prompt/params');
        set({ promptParams: response.data });
      } catch (error) {
        console.error('API 요청 실패:', error);
        throw error;
      }
    },

    // 프롬프트 등록
    postPrompt: async (promptData, navigate) => {
      set({ prompts: [] }); // 상품 목록 초기화

      try {
        const response = await apiClient.post('/prompt', promptData);
        if (response.status === 200) {
          toast.success('프롬프트가 성공적으로 등록되었습니다!');
          navigate('/prompt');
        }
      } catch (error) {
        if (isAxiosError<ResponseError>(error)) {
          toast.error(error.response?.data.errorMsg);
        } else {
          console.error('프롬프트 등록 실패:', error);
          toast.error('프롬프트 등록에 실패했습니다.');
        }
      }
    },

    // 프롬프트 업데이트
    updatePrompt: async (id, promptData) => {
      set({ prompts: [] }); // 프롬프트 목록 초기화

      try {
        const response = await apiClient.patch(`/prompt/${id}`, promptData);
        if (response.status === 200) {
          toast.success('프롬프트가 성공적으로 수정되었습니다!');
        }
      } catch (error) {
        if (isAxiosError<ResponseError>(error)) {
          toast.error(error.response?.data.errorMsg);
        } else {
          console.error('프롬프트 수정 실패:', error);
          toast.error('프롬프트 수정에 실패했습니다.');
        }
      }
    },

    downloadPromptExcelTemplate: async () => {
      try {
        const response = await apiClient.get('/prompt/upload-excel-template', {
          responseType: 'blob',
        });

        const url = URL.createObjectURL(
          new Blob([response.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          })
        );
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
          'download',
          `Prompt-Upload-Template-${formatDate(new Date())['yyyy-mm-dd']}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('프롬프트 엑셀 양식 다운로드 실패:', error);
        toast.error('프롬프트 엑셀 양식을 다운로드하지 못했습니다.');
      }
    },

    uploadPromptExcel: async file => {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await apiClient.post<PromptExcelUploadResponse>(
          '/prompt/upload-excel',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );

        const { successCount, failCount, totalRows, errors } = response.data;

        if (totalRows === 0) {
          toast.error('엑셀에 등록할 프롬프트가 없습니다.');
          return;
        }

        if (failCount > 0) {
          const firstError = errors[0];
          toast.error(
            `총 ${totalRows}건 중 ${failCount}건 실패: ${firstError.rowNumber}행 ${firstError.errorMsg}`
          );
          return;
        }

        toast.success(`프롬프트 ${successCount}건이 성공적으로 등록되었습니다.`);
        await get().getPrompts();
      } catch (error) {
        if (isAxiosError<ResponseError>(error)) {
          toast.error(error.response?.data.errorMsg);
        } else {
          console.error('프롬프트 엑셀 업로드 실패:', error);
          toast.error('프롬프트 엑셀 업로드에 실패했습니다.');
        }
      }
    },
  })
);
